import { notify } from '@billy/design-system'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import { IntegrationStatus } from '@features/bills/types/integrationStatus'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { ExpensesTrackingEvents } from '@views/receipts/enums/tracking'

import { NotificationKeys } from '../../../../src/enums/notificationKeys'
import { QueryKeys } from '../../../../src/enums/queryKeys'
import { useSegment } from '../../../../src/hooks'
import { postV2Request } from '../../../../src/utils'

/**
 * Triggers a retry of the current bill integration sync.
 * Uses integrationStatus as a parameter to force a semi-coherent API
 * and not allow users to pass a random un-integrated bill
 */
export const useRetryBillIntegrationSyncMutation = ({
  integrationStatus,
}: {
  integrationStatus?: IntegrationStatus
}) => {
  const { track } = useSegment()
  const { organization } = useUserOrganization()
  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const {
    mutate: retryIntegration,
    isLoading,
    ...rest
  } = useMutation(
    [`retry-integration-${integrationStatus?.billId}`],
    () => {
      if (!organization) {
        return Promise.resolve()
      }

      track(ExpensesTrackingEvents.BillSyncRetryButtonClick)

      return postV2Request({
        endpoint: `/v2/organizations/${organization.id}/bills/${integrationStatus?.billId}/sync-to-cegid`,
        headers: new Headers(),
        payload: {},
      })
    },
    {
      onError: () => {
        notify({
          id: NotificationKeys.BillIntegrationSyncRetryFailed,
          message: t('bill.action.retry_sync.failed'),
          variant: 'error',
        })
      },
      onSuccess: () => {
        // refresh attachments
        queryClient.invalidateQueries(QueryKeys.AttachmentsList)
        queryClient.invalidateQueries(QueryKeys.Bills)
        queryClient.invalidateQueries([QueryKeys.Bill, integrationStatus?.billId])
        notify({
          id: NotificationKeys.BillIntegrationSyncRetrySuccess,
          message: t('bill.action.retry_sync.success'),
          variant: 'success',
        })
      },
    },
  )

  return {
    retryIntegration,
    isLoading,
    ...rest,
  }
}
